import { readChatImageCount } from "@/apis/chat";
import i18n from "@/locales";
import api, { UpdateMateDetailParams } from "@/modules/api";
import { mergeIgnoreEmpty } from "@/modules/common";
import { useGlobalUser } from "@/stores/globalUser";
import { PER_PAGE, useMoment } from "@/stores/moment";
import { Mate, MateLink } from "@/types/entity";
import { useLocalStorage, useSessionStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showNotify,
  showSuccessToast,
} from "vant";
import { unref } from "vue";
import { useNotification } from "./notification";
import { MateType } from "@/enums/mateEnums";
import { DriftCapsule, DriftCategory } from "@/types/mate";
import {
  DriftCategoryType,
  readDriftCapsulePage,
  readDriftCategoryPage,
  salvageOneDriftCapsule,
} from "@/apis/mate";
import { useGlobalModal } from "./globalModal";

const defaultDiscover = () => ({
  isFetching: false,
  isFinished: false,
  page: 1,
  scrollTop: 0,
  data: [] as Mate[],
});

const mateSorter = (a: Mate, b: Mate) => {
  if (a.isUser && !b.isUser) return -1;
  if (!a.isUser && b.isUser) return 1;
  const notification = useNotification();
  const aN = notification.getUnreadCountByMateId(a.id!);
  const bN = notification.getUnreadCountByMateId(b.id!);
  if (aN !== bN) return bN - aN;
  if (a.name && !b.name) return -1;
  if (!a.name && b.name) return 1;
  if (a.mediaUrl && !b.mediaUrl) return -1;
  if (!a.mediaUrl && b.mediaUrl) return 1;
  return 0;
};

export const useMate = defineStore("mate", {
  state: () => {
    const cachedUser = useSessionStorage<User | null>("user", null);
    const cachedMateList = cachedUser.value
      ? unref(useLocalStorage(`mateList:${cachedUser.value.id}`, [] as Mate[]))
      : [];
    return {
      currentMate:
        cachedMateList.find((mate) => mate.id === cachedUser.value?.mateId) ??
        null,
      uuidList: cachedMateList.map((mate) => mate.uuid),
      mateMap: new Map<string, Mate>(
        cachedMateList.map((mate) => [mate.uuid, mate])
      ),
      mateIdMap: new Map<number, Mate>(
        cachedMateList.map((mate) => [mate.id!, mate])
      ),
      changed: false,
      enableCreateStory: null as boolean | null,
      galleryCurrentMate: null as Mate | null,
      currentTab: "hot" as "hot" | "new" | "my",
      discoverList: {
        hot: defaultDiscover(),
        new: defaultDiscover(),
        my: defaultDiscover(),
      },
      mateHomeTab: "profile",

      friendMateMap: new Map<number, Mate>(),
      mateLinkList: [] as MateLink[],
      aiMatePage: 1,
      aiMateList: [] as Mate[],
      selfAvatarList: [] as Mate[],

      // Drift
      driftCategoryList: [] as DriftCategory[],
      driftCapsuleList: [] as DriftCapsule[],
      showDriftCategoryModal: false,
    };
  },
  getters: {
    // user(state): Mate | undefined {
    //   const uuid = state.uuidList.find(
    //     (uuid) => state.mateMap.get(uuid)!.isUser
    //   );
    //   return uuid ? state.mateMap.get(uuid)! : undefined;
    // },
    fullList(state): Mate[] {
      // Notic: 这里不要使用mateSorter排序，内部notificaionStore的watch依赖fullList，会导致循环引用报错
      return state.uuidList.map((uuid) => state.mateMap.get(uuid)!);
    },
    list(): Mate[] {
      // return this.fullList.filter((mate) => !mate.isUser).sort(mateSorter);
      return this.fullList.filter((mate) => !mate.isUser);
    },
    listWithSelfAvatar(): Mate[] {
      return [...this.list, ...this.selfAvatarList];
    },
    fullListWithSelfAvatar(): Mate[] {
      return [...this.fullList, ...this.selfAvatarList];
    },
    listWithUser(): Mate[] {
      // return this.fullList.sort(mateSorter);
      return this.fullList;
    },
    userMate(): Mate {
      return this.fullList.find((mate) => mate.isUser)!;
    },
    userMateEmojiCount(): number {
      return this.list.reduce((acc, mate) => acc + mate.emojiCount, 0);
    },
    canDelete() {
      return (mateId: number) => {
        const publishedMate: Mate[] = this.list.filter(
          (item) => item.status === 1
        );
        if (publishedMate.length > 1) {
          return true;
        }
        return (
          publishedMate.length <= 1 &&
          !publishedMate.find((item) => item.id === mateId)
        );
      };
    },
    currentList(state) {
      return state.discoverList[state.currentTab];
    },
    totalLikes() {
      const list: Mate[] = this.fullList;
      return list.reduce((acc: number, mate: Mate) => acc + mate.emojiCount, 0);
    },
  },
  actions: {
    toMateHome(mate: Mate, isEdit = false) {
      if (mate.type === MateType.SelfAvatar || mate.type === MateType.User) {
        return window.$router.push({
          name: "treeholeSelfSetting",
          query: {
            mateUuid: mate.uuid,
          },
        });
      } else {
        return window.$router.push({
          name: isEdit ? "mateEdit" : "mateHome",
          params: { uuid: mate.uuid },
        });
      }
    },

    /**
     * 获取Mate列表
     * 1. 如果已经请求过，直接返回
     * 2. 如果没有请求过，请求并返回
     * @param force 是否强制刷新
     */
    async getMateList(force = false): Promise<Mate[]> {
      if (!this.changed && this.list.length > 0 && !force) {
        // 1. 内存中有Mate列表，且没有变化 => 直接返回
        return this.list;
      }
      // else if (!force && cachedMateList) {
      //   // 2. 本地缓存中有Mate列表 => 直接返回 + 异步请求
      //   this.list = cachedMateList;
      //   await this.resetCurrentMate(cachedMateList);
      //   this.fetchMateList(false);
      //   return cachedMateList;
      // }
      else {
        return this.fetchMateList();
      }
    },
    updateMateMap(mateList: Mate[], updateList = true) {
      if (updateList) {
        this.uuidList = mateList.map((mate) => mate.uuid);
      }
      mateList.forEach((mate) => {
        const old = this.mateMap.get(mate.uuid);
        if (old) {
          mergeIgnoreEmpty(old, mate);
          if (mate.spritezz?.length !== old.spritezz?.length) {
            old.spritezz = mate.spritezz;
          }
        } else {
          this.mateMap.set(mate.uuid, mate);
          this.mateIdMap.set(mate.id!, mate);
        }
      });
    },
    async fetchMateListSimple(): Promise<Mate[]> {
      const res = await api.readMatePage({
        sort: { field: "id", order: "desc" },
        pagination: { page: 1, perPage: 999 },
      });
      const mateList = res.page.data;
      this.updateMateMap(mateList);
      this.changed = false;
      const cachedMateList = useLocalStorage(
        `mateList:${useGlobalUser().id}`,
        {} as Mate[]
      );
      cachedMateList.value = mateList;
      return mateList;
    },

    async fetchSelfAvatarList() {
      const res = await api.readMatePage({
        filter: { type: MateType.SelfAvatar },
        pagination: { page: 1, perPage: 999 },
      });
      this.selfAvatarList = res.page.data;
      this.updateMateMap(res.page.data, false);
    },

    async fetchAiMateList(reload = false) {
      if (reload) {
        this.aiMateList = [];
        this.aiMatePage = 1;
      }
      const page = this.aiMatePage;

      const res = await api.readAiMatePage({
        filter: { excludeLinked: 1 },
        pagination: { page, perPage: PER_PAGE },
      });
      this.aiMateList = [...this.aiMateList, ...res.page.data];
      this.updateMateMap(res.page.data, false);
      this.aiMatePage++;
      return res.page.data.length === PER_PAGE;
    },
    /**
     * 从服务端获取Mate列表
     */
    async fetchMateList(shouldReset = true): Promise<Mate[]> {
      const [mateList] = await Promise.all([
        this.fetchMateListSimple(),
        this.fetchSelfAvatarList(),
      ]);
      if (mateList.length === 0) {
        // TODO: 跳转到创建Mate的页面
      }
      if (shouldReset) {
        await this.resetCurrentMate();
      }
      return mateList;
    },
    async resetCurrentMate() {
      const mateList = this.fullListWithSelfAvatar;
      const globalUser = useGlobalUser();
      if (!globalUser.currentMateId || globalUser.currentMateId === 0) {
        globalUser.currentMateId = mateList[0].id!;
        this.currentMate = mateList[0];
      } else {
        const targetMate = mateList.find(
          (mate) => mate.id === globalUser.currentMateId
        );
        if (targetMate) {
          this.currentMate = targetMate;
          // this.initMateModuleOrder();
        } else {
          await this.changeCurrentMate(mateList[0].id!);
        }
      }
    },
    async refreshCurrentMate() {
      if (this.currentMate) {
        const res = await api.readOneMate(this.currentMate.uuid);
        this.currentMate = res.item;
        const listItem = this.list.find(
          (mate) => mate.uuid === this.currentMate!.uuid
        );
        if (listItem) {
          Object.assign(listItem, res.item);
        }
      }
    },
    /**
     * 切换当前Mate
     * @param mateId
     */
    async changeCurrentMate(mateId: number) {
      if (this.currentMate?.id === mateId) return;
      if (!this.isMyMateById(mateId)) return;

      const mate = this.mateIdMap.get(mateId)!;
      if (!mate) {
        throw new Error("Mate not found");
      }
      this.changeCurrentMateLocally(mate);
      useGlobalUser().updateUserApi({ mateId });

      this.currentMate = mate;

      // this.initMateModuleOrder();
    },
    changeCurrentMateLocally(mate: Mate) {
      this.currentMate = mate;
      useGlobalUser().currentMateId = mate.id!;
    },
    /**
     * 切换当前GalleryMate
     * @param mateId
     */
    async changeGalleryCurrentMate(mateId: number) {
      const mate = this.list?.find((mate) => mate.id === mateId);
      if (!mate) {
        throw new Error("Mate not found");
      }
      this.galleryCurrentMate = mate;
    },
    /** 删除当前选中的Mate，并重新设置当前Mate */
    async deleteCurrentMate(mate: Mate): Promise<void> {
      const isDeleteCurrentMate = mate.uuid === this.currentMate!.uuid;
      if (isDeleteCurrentMate) {
        await this.changeCurrentMate(this.userMate!.id!);
      }
      // 删除Mate
      await api.deleteOneMate(mate.uuid);
      // 切换Mate
      this.uuidList = this.uuidList.filter((uuid) => uuid !== mate.uuid);
      await this.fetchMateList();
    },
    /** 更新Mate的Tone */
    async updateMate(
      uuid: string,
      params: Partial<
        Pick<
          Mate,
          | "mateToneId"
          | "gender"
          | "isSuperAdmireEnabled"
          | "moduleOrder"
          | "currentWorldviewId"
        >
      >
    ): Promise<Mate | null> {
      const targetMate = this.mateMap.get(uuid);
      if (!targetMate) {
        showNotify({ type: "danger", message: "Mate not found" });
        return null;
      }
      const res = await api.updateMate(uuid, params);
      mergeIgnoreEmpty(targetMate, res.item);
      return targetMate;
    },
    /** 创建Mate */
    async createMate(data: Partial<Mate> = {}) {
      const res = await api.createOneMate(data);
      await this.fetchMateList();
      return res.item;
    },
    async clickCreateMate(autoToMateEdit = true) {
      const toast = showLoadingToast({
        message: i18n.global.t("Message.preparingToCreateCharacter"),
        duration: 0,
      });
      try {
        const mate = await this.createMate();
        await Promise.all([
          this.changeCurrentMate(mate.id!),
          this.fetchMateList(),
        ]);
        if (autoToMateEdit) {
          await window.$router.push({
            name: "mateEdit",
            params: { uuid: mate.uuid },
          });
        }
        return mate;
      } finally {
        toast.close();
      }
    },

    async checkHasMate(fn?: () => void) {
      if (this.list.length === 0) {
        try {
          await showConfirmDialog({
            message: i18n.global.t("roleplay.noMate"),
            confirmButtonText: i18n.global.t("Buttons.createMate"),
            cancelButtonText: i18n.global.t("Buttons.cancel"),
          });
          await this.clickCreateMate();
        } catch {
          fn?.();
        }
        return false;
      }
      return true;
    },
    /** [尽量只在store内部使用] 更新Mate的Detail */
    async updateMateDetail(
      uuid: string,
      params: Partial<UpdateMateDetailParams>
    ): Promise<Mate | null> {
      const targetMate = this.mateMap.get(uuid);
      if (!targetMate) {
        showNotify({ type: "danger", message: "Mate not found" });
        return null;
      }
      const res = await api.updateMateDetail(uuid, params);
      mergeIgnoreEmpty(targetMate, res.item);
      return targetMate;
    },
    async publish(uuid: string, withConfirm = true) {
      const targetMate = this.mateMap.get(uuid);
      if (!targetMate) {
        showNotify({ type: "danger", message: "Mate not found" });
        return null;
      }
      if (!targetMate.worldviewId) {
        showNotify({
          type: "danger",
          message: i18n.global.t("Message.publishMateFillWorldview"),
        });
        return;
      }
      if (!targetMate.name) {
        showNotify({
          type: "danger",
          message: i18n.global.t("Message.characterNameRequired"),
        });
        return;
      }
      let isSyncToInteractionPool = false;
      if (withConfirm) {
        try {
          await showConfirmDialog({
            title: i18n.global.t("Message.enableEnterPoolConfirm"),
            message: i18n.global.t("Message.enableEnterPoolConfirmDesc", {
              label: i18n.global.t("interactive.poolTipsDesc"),
            }),
            confirmButtonText: i18n.global.t("Buttons.publishAndEnterPool"),
            cancelButtonText: i18n.global.t("Buttons.publishOnly"),
          });
          isSyncToInteractionPool = true;
        } catch (e) {
          isSyncToInteractionPool = false;
        }
      }
      const toast = showLoadingToast({
        message: i18n.global.t("Message.publishing"),
        duration: 0,
      });
      try {
        const res = await api.publishOneMate(uuid, isSyncToInteractionPool);
        mergeIgnoreEmpty(targetMate, res.item);
        await this.fetchMateList();
        showSuccessToast({
          message: i18n.global.t("Message.publishSuccess"),
          duration: 500,
        });
        return targetMate;
      } catch (e) {
        toast.close();
        showFailToast(i18n.global.t("Message.publishFailed"));
        throw e;
      }
    },
    async fetchMateItem(uuid: string) {
      const res = await api.readOneMate(uuid);
      let mate = res.item;
      if (this.mateMap.has(uuid)) {
        const oldMate = this.mateMap.get(uuid)!;
        mergeIgnoreEmpty(oldMate, res.item);
        this.mateMap.set(uuid, oldMate);
        this.mateIdMap.set(res.item.id!, oldMate);
        mate = oldMate;
      } else {
        this.mateMap.set(uuid, res.item);
        this.mateIdMap.set(res.item.id!, res.item);
      }
      return mate;
    },
    isMyMate(uuid: string): boolean {
      return this.fullListWithSelfAvatar.some((mate) => mate.uuid === uuid);
    },
    isMyMateById(id?: number): boolean {
      if (!id) return false;
      return this.fullListWithSelfAvatar.some((mate) => mate.id === id);
    },
    async toggleAgentChat(uuid: string, enabled: boolean) {
      await this.updateMateDetail(uuid, { agentEnabled: enabled ? 1 : 0 });
    },
    async getOrFetchMate(uuid: string, force = false) {
      const targetMate = this.mateMap.get(uuid);
      if (!force && targetMate) {
        return targetMate;
      }
      return this.fetchMateItem(uuid);
    },
    getMyMateById(id: number) {
      return this.fullListWithSelfAvatar.find((mate) => mate.id === id);
    },
    getMyOrFriendMateById(id: number) {
      return this.friendMateMap.get(id) || this.getMyMateById(id);
    },
    async canCreateStory(): Promise<{ enable: boolean; currentCount: number }> {
      if (this.enableCreateStory) {
        return { enable: true, currentCount: 0 };
      }
      const momentStore = useMoment();
      const res = await momentStore.fetchMomentByMateId(this.currentMate!.id!);
      this.enableCreateStory = res.length >= 5;
      return { enable: this.enableCreateStory, currentCount: res.length };
    },

    async fetchPopularMateList(): Promise<void> {
      const { data, isFinished, isFetching, page } = this.currentList;
      if (isFetching || isFinished) return;
      this.currentList.isFetching = true;
      const resp = await api.readMatePopularPage({
        tab: this.currentTab,
        pagination: { perPage: 12, page },
      });
      this.currentList.page++;
      const respData = resp.page.data;
      if (respData.length < 12) {
        this.currentList.isFinished = true;
      }

      const tmpList: Mate[] = respData
        .map((v: Mate) => {
          const { mediaUrl, gender, name, bio, uuid, type, isNsfw } = v;
          return {
            ...v,
            uuid,
            src: mediaUrl,
            gender: gender,
            ratio: "3/4",
            title: name,
            description: bio,
            url: `/mate/${uuid}`,
            type,
            isNsfw,
          };
        })
        .sort(() => Math.random() - 0.5);
      this.currentList.data = [...data, ...tmpList];
      setTimeout(() => {
        this.currentList.isFetching = false;
      }, 500);
    },
    async goCurrentMateHome() {
      if (!this.currentMate) return;
      return await window.$router.push({
        name: "mateHome",
        params: { uuid: this.currentMate!.uuid },
      });
    },
    async refreshChatImageCount(uuid: string) {
      const { data } = await readChatImageCount(uuid);
      const mate = this.mateMap.get(uuid);
      if (mate) {
        mate.chatImageMessageCount = data.count;
      }
    },

    async fetchOCListByUserMateId(userMateUuid: string) {
      const res = await api.readMateListByUserMateId(userMateUuid);
      return res.itemzz;
    },

    async fetchMateLinkList(isActive?: boolean) {
      const res = await api.readMateLinkPage({
        filter: { isActive },
        pagination: { page: 1, perPage: 999 },
      });
      this.mateLinkList = res.page.data;
    },

    async createMateLink(targetMateId: number) {
      const res = await api.createOneMateLink({ targetMateId });
      res.item.targetMate = this.mateIdMap.get(targetMateId);
      this.mateLinkList.push(res.item);

      const idx = this.aiMateList.findIndex((item) => item.id === targetMateId);
      if (idx !== -1) {
        this.aiMateList.splice(idx, 1);
      }

      return res.item;
    },

    async updateMateLink(id: number, isActive: boolean) {
      await api.updateOneMateLink(id, { isActive });
      const index = this.mateLinkList.findIndex((item) => item.id === id);
      if (index !== -1) {
        this.mateLinkList[index].isActive = isActive;
      }
    },

    async deleteMateLink(id: number) {
      await api.deleteOneMateLink(id);
      const index = this.mateLinkList.findIndex((item) => item.id === id);
      if (index !== -1) {
        const removed = this.mateLinkList.splice(index, 1);
        const removeMate = removed[0].targetMate;
        if (removeMate && !this.isMyMate(removeMate.uuid)) {
          this.aiMateList.unshift(removeMate);
        }
      }
    },

    async importManyMateLink(mateIdzz: number[]) {
      const res = await api.importManyMateLink({ mateIdzz });
      this.mateLinkList.push(...res.itemzz);
    },

    async fetchDriftCategoryList(type: DriftCategoryType) {
      const res = await readDriftCategoryPage({
        filter: { type },
        pagination: { page: 1, perPage: 999 },
      });
      this.driftCategoryList = res.page.data;
    },

    async fetchDriftCapsuleList(categoryId?: number) {
      const res = await readDriftCapsulePage({
        filter: { categoryId: categoryId || undefined },
        pagination: { page: 1, perPage: 999 },
      });
      this.driftCapsuleList = res.page.data;
    },

    async postSalvageDriftCapsule(categoryId: number) {
      const res = await salvageOneDriftCapsule(categoryId);
      this.driftCapsuleList.push(res.item);
      return res.item;
    },

    async clickEnterDriftCapsule() {
      const user = useGlobalUser();
      if (!user.isLogin) return user.openLoginModal("register");
      const loading = showLoadingToast(i18n.global.t("Message.loading"));

      try {
        await Promise.all([
          this.fetchDriftCapsuleList(),
          this.fetchDriftCategoryList("salvageable"),
        ]);

        let categoryId: number | undefined = undefined;
        if (this.driftCapsuleList.length === 0) {
          // 没有漂流仓，查找有没有可以打捞的领域
          if (this.driftCategoryList.length === 0)
            return showFailToast(i18n.global.t("Message.noDriftCategory"));
          categoryId = this.driftCategoryList[0].id;
        }

        let tab: string | undefined = undefined;

        if (this.driftCategoryList.length === 0) {
          tab = "salvaged";
        }

        loading.close();
        window.$router.push({
          name: "driftCapsuleHome",
          query: {
            categoryId,
            tab,
          },
        });
      } catch {
        loading.close();
      }
    },

    async clickCreateMateOption() {
      const userStore = useGlobalUser();
      if (!userStore.isLogin) return userStore.openLoginModal("register");
      useGlobalModal().openCustomGlobalActionSheet({
        title: "你想通过哪种方式获取伙伴？",
        list: [
          {
            content: "<i class='iconify mingcute--add-line'></i> 简单模式",
            className: "btn-primary",
            callback: async () => {
              window.$router.push({
                name: "mateCreateAuto",
              });
            },
          },
          {
            content: "<i class='iconify mingcute--empty-box-fill'></i> 领养",
            callback: () => {
              window.$router.push({
                name: "adoptionHome",
              });
            },
          },
          {
            content: "<i class='iconify mingcute--grid-line'></i> OC编辑器",
            callback: async () => {
              if (this.list.some((v) => !v.name)) {
                try {
                  await showConfirmDialog({
                    message: "您还有未命名的伙伴，确定要创建一个新的伙伴吗？",
                    confirmButtonText: "是的",
                    cancelButtonText: "不",
                  });
                } catch {
                  return;
                }
              }
              this.clickCreateMate();
            },
          },
        ],
      });
    },
  },
});
